import React, { MutableRefObject, useMemo, useRef } from 'react'
import {
  ContentCard,
  Flex,
  TableColumn,
  TableSquare,
  useScrollVisible,
  Text,
  TextVariant,
  useInfiniteScrollData,
  Loading
} from '@mprolab/uikit'
import styled, { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { CycleLogActivityData } from '@mprolab/sdk'
import { MediaQuery } from '@mprolab/uikit/src/theme/base'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { useLifetimeTokens } from 'state/global/hooks'
import { useGetCycleLogActivitesQuery } from 'state/services/indexNode/indexNode'
import { observer } from 'mobx-react-lite'
import { formatCycleTimestampUtc, formatPrice } from '../rewards.logic'

const ActiveLogWrapper = styled(ContentCard)`
  flex-direction: column;
`

const List = styled(Flex)<{ scrollable: boolean }>`
  overflow-y: auto;
  max-height: 543px;
  flex-direction: column;
  padding-right: ${({ scrollable }) => (scrollable ? '16px' : 'unset')};
  width: 100%;
`

const CycleLogs: React.FC = observer(() => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const wrapper = useRef<HTMLDivElement>(null)
  const scrollVisible = useScrollVisible(wrapper as MutableRefObject<HTMLDivElement>)
  const { account } = useWeb3React()
  const lifetimeTokens = useLifetimeTokens()
  const { dataLengthToFetch, loadMoreData } = useInfiniteScrollData()
  const accessToken = lifetimeTokens?.[account]

  const { data: cycleActivities, isLoading } = useGetCycleLogActivitesQuery({
    account,
    accessToken,
    limit: dataLengthToFetch
  }, {
    skip: !account || !accessToken,
    refetchOnMountOrArgChange: true
  })

  const columns = useMemo(
    (): TableColumn[] => [
      {
        key: 'startTime',
        name: t('rewards.cycleStartTime'),
        gridColumns: 2.4,
        customComponent(log: CycleLogActivityData) {
          return (
            <Text variant={TextVariant.BODY_SMALL} isTrimmed linesToDisplay={1}>
              {formatCycleTimestampUtc(log.cycleStartTimestamp)}
            </Text>
          )
        }
      },
      {
        key: 'endTime',
        name: t('rewards.cycleEndTime'),
        gridColumns: 2.4,
        customComponent(log: CycleLogActivityData) {
          return (
            <Text variant={TextVariant.BODY_SMALL} isTrimmed linesToDisplay={1}>
              {formatCycleTimestampUtc(log.cycleEndTimestamp)}
            </Text>
          )
        }
      },
      {
        key: 'nodeCredits',
        name: t('rewards.nodeCredits'),
        gridColumns: 1.7,
        align: 'flex-end',
        customComponent(log: CycleLogActivityData) {
          return (
            <Flex justifyContent="flex-end">
              <Text variant={TextVariant.BODY_SMALL_BOLD} isTrimmed linesToDisplay={1}>
                {formatPrice(log.nodesCredits)}
              </Text>
            </Flex>
          )
        }
      },
      {
        key: 'boosterCredits',
        name: t('rewards.boosterCredits'),
        gridColumns: 1.7,
        align: 'flex-end',
        customComponent(log: CycleLogActivityData) {
          return (
            <Flex justifyContent="flex-end">
              <Text variant={TextVariant.BODY_SMALL_BOLD} isTrimmed linesToDisplay={1}>
                {formatPrice(log.boosterCredits)}
              </Text>
            </Flex>
          )
        }
      },
      {
        // Booster reward in MPRO — derived server-side from booster credits
        // and the cycle's dailyRewardPerCredit. Shown side-by-side with the
        // total `reward` so users can see how much of the cycle's payout
        // came from the upline booster (per ADR 0002 — booster value is
        // always transparent in the log).
        key: 'boosterReward',
        name: t('rewards.boosterReward'),
        gridColumns: 1.7,
        align: 'flex-end',
        customComponent(log: CycleLogActivityData) {
          return (
            <Flex justifyContent="flex-end">
              <Text variant={TextVariant.BODY_SMALL_BOLD} isTrimmed linesToDisplay={1}>
                {formatPrice(log.boosterReward ?? 0)}
              </Text>
            </Flex>
          )
        }
      },
      {
        key: 'rewardAggregated',
        name: `${t('rewards.reward')} [MPRO]`,
        gridColumns: 1.7,
        align: 'flex-end',
        customComponent(log: CycleLogActivityData) {
          return (
            <Flex justifyContent="flex-end">
              <Text variant={TextVariant.BODY_SMALL_BOLD} isTrimmed linesToDisplay={1} color={colors.cyan}>
                {formatPrice(log.reward)}
              </Text>
            </Flex>
          )
        }
      }
    ],
    [colors.cyan, t]
  )

  return (
    <>
      <ActiveLogWrapper>
        <List ref={wrapper} scrollable={scrollVisible} id="activeLogs">
          <InfiniteScroll
            next={loadMoreData}
            hasMore={dataLengthToFetch < cycleActivities?.count}
            loader={<Loading />}
            dataLength={dataLengthToFetch}
            scrollThreshold={0.99}
            scrollableTarget="activeLogs"
          >
            <TableSquare
              tableTitle={t('rewards.cycleLog')}
              plain
              loading={isLoading}
              data={cycleActivities?.data}
              emptyListComponent={<Text textAlign="center">{t('rewards.noActiveLogs')}</Text>}
              fullViewMediaquery={MediaQuery.tablet}
              fullViewCustomWidth={900}
              {...{ columns }}
            />
          </InfiniteScroll>
        </List>
      </ActiveLogWrapper>
    </>
  )
})

export default CycleLogs
